import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation } from "@tanstack/react-query"
import { CalendarDays, Building2, CheckCircle2, XCircle, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  getPublicInstructorInvitation,
  respondPublicInstructorInvitation,
  type InstructorInvitationStatus,
} from "@/services/public-instructor-invitation"
import { TenantClosedNotice } from "@/components/TenantClosedNotice"
import { isTenantClosedError } from "@/lib/tenant-closed"
import { formatIsoDateFr } from "@/lib/organisation-day"

function formatDate(iso: string): string {
  return formatIsoDateFr(iso, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })
}

function Shell({ company, children }: { company?: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50" data-test="public-shell">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4 sm:px-6">
          <span className="flex h-10 w-10 items-center justify-center rounded bg-slate-100 text-slate-500">
            <Building2 className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Invitation formateur
            </p>
            <p className="truncate text-sm font-medium text-slate-900" data-test="public-tenant-name">
              {company ?? "Organisme de formation"}
            </p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">{children}</main>
      <footer className="mx-auto max-w-2xl px-4 py-6 text-center text-xs text-muted-foreground sm:px-6">
        Géré par Qualneo
      </footer>
    </div>
  )
}

export default function PublicInstructorInvitationPage() {
  const { token } = useParams<{ token: string }>()
  const [showDecline, setShowDecline] = useState(false)
  const [declineReason, setDeclineReason] = useState("")
  const [done, setDone] = useState<InstructorInvitationStatus | null>(null)

  const query = useQuery({
    queryKey: ["public-instructor-invitation", token],
    queryFn: () => getPublicInstructorInvitation(token!),
    enabled: Boolean(token),
    retry: false,
  })

  const respondMutation = useMutation({
    mutationFn: (accept: boolean) =>
      respondPublicInstructorInvitation(token!, accept, accept ? undefined : declineReason),
    onSuccess: (res) => setDone(res.status),
  })

  if (query.isLoading) {
    return (
      <Shell>
        <div className="flex items-center justify-center py-16 text-muted-foreground" data-test="invitation-loading">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Chargement de l'invitation...
        </div>
      </Shell>
    )
  }

  if (isTenantClosedError(query.error)) {
    return (
      <Shell>
        <TenantClosedNotice />
      </Shell>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Shell>
        <Card data-test="invitation-error">
          <CardContent className="space-y-2 py-10 text-center">
            <XCircle className="mx-auto h-10 w-10 text-rose-400" />
            <p className="font-medium" data-test="invitation-error-title">
              Invitation introuvable ou expirée
            </p>
            <p className="text-sm text-muted-foreground" data-test="invitation-error-message">
              Ce lien n'est plus valide. Rapprochez-vous de l'organisme de formation pour recevoir
              une nouvelle invitation.
            </p>
          </CardContent>
        </Card>
      </Shell>
    )
  }

  const invitation = query.data
  const effectiveStatus: InstructorInvitationStatus = done ?? invitation.status
  const alreadyAnswered = effectiveStatus !== "pending"

  return (
    <Shell company={invitation.company_name}>
      <Card>
        <CardHeader>
          <CardTitle data-test="invitation-session-title">{invitation.session_title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p>
            Bonjour <span className="font-medium">{invitation.instructor_name}</span>,
          </p>
          <p>
            <span className="font-medium">{invitation.company_name}</span> vous a positionné comme
            formateur sur cette session et vous demande de confirmer votre intervention.
          </p>
          <div className="flex items-center gap-2 rounded-md border bg-slate-50 px-3 py-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <span data-test="invitation-dates">
              {invitation.start_date === invitation.end_date
                ? formatDate(invitation.start_date)
                : `Du ${formatDate(invitation.start_date)} au ${formatDate(invitation.end_date)}`}
            </span>
            <span className="ml-auto text-xs text-muted-foreground">{invitation.session_code}</span>
          </div>

          {effectiveStatus === "confirmed" && (
            <div
              className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-900"
              data-test="invitation-confirmed"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <p className="text-sm">
                Votre intervention est <span className="font-semibold">confirmée</span>. Merci !
                L'organisme en est informé.
              </p>
            </div>
          )}

          {effectiveStatus === "declined" && (
            <div
              className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-900"
              data-test="invitation-declined"
            >
              <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <p className="text-sm">
                Vous avez <span className="font-semibold">décliné</span> cette intervention.
                L'organisme en est informé.
              </p>
            </div>
          )}

          {!alreadyAnswered && !showDecline && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                className="flex-1"
                onClick={() => respondMutation.mutate(true)}
                disabled={respondMutation.isPending}
                data-test="invitation-accept-button"
              >
                {respondMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}
                Confirmer mon intervention
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDecline(true)}
                disabled={respondMutation.isPending}
                data-test="invitation-decline-button"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Décliner
              </Button>
            </div>
          )}

          {!alreadyAnswered && showDecline && (
            <div className="space-y-2" data-test="invitation-decline-form">
              <Label htmlFor="decline-reason">Motif (facultatif)</Label>
              <Textarea
                id="decline-reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Indisponibilité, conflit d'agenda..."
                rows={3}
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={() => respondMutation.mutate(false)}
                  disabled={respondMutation.isPending}
                  data-test="invitation-decline-confirm-button"
                >
                  {respondMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Confirmer le refus
                </Button>
                <Button
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setShowDecline(false)}
                  disabled={respondMutation.isPending}
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {respondMutation.isError && (
            <p className="text-sm text-rose-600" data-test="invitation-respond-error">
              Une erreur est survenue. Réessayez ou contactez l'organisme.
            </p>
          )}
        </CardContent>
      </Card>
    </Shell>
  )
}
